import Flex from "../../../core/components/Flex";
import {Badge, Button, Dropdown, Select, Tooltip} from "antd";
import {
    AlertOutlined,
    CheckOutlined, CopyOutlined, DeleteOutlined,
    DownOutlined,
    EyeOutlined,
    FieldTimeOutlined,
    LockOutlined,
    ExclamationCircleOutlined
} from "@ant-design/icons";
import {BsFillMicFill} from "react-icons/bs";
import {dateFormat, getAge} from "../../../core/utils/date-utils";
import StudyRowMenu from "./StudyRowMenu";
import {HiOutlineDocumentText} from 'react-icons/hi'
import {BsFileArrowDownFill} from 'react-icons/bs'
import {t} from "i18next";
import {PERMISSIONS} from "../../../core/context/UserContext";
import StudyTypeEnum from "../../../enums/StudyTypeEnum";
import StudyStatusEnum from "../../../enums/StudyStatusEnum";
import EnumHelper from "../../../enums/EnumHelper";
import InstitutionCell from "./InstitutionCell";
import StudyListStar from "../../study-list/StudyListStar";
import moment from "moment";
import {copyToClipboard} from "../../../core/utils/clipboard-utils";

const StudyTaskTypeEnum = {
    1: 'MISSING_IMAGE',
    2: 'RE_ASSESSMENT'
}

const studyGridColumns = (refresh, hasPermission) => {
    let columns =  [
        {
            title: 'Id',
            key: 'id',
            width: 80,
            render: (text ,row) => (
                <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                    <StudyRowMenu row={row} />
                    <Button
                        size={'small'}
                        type={'text'}
                        icon={<CopyOutlined />}
                        style={{color: '#7f8c8d'}}
                        onClick={async () => {
                            const text = [
                                `ID: ${row.id}`,
                                `${t('Patient ID')}: ${row.dicomPatientId}`,
                                `${t('Patient Name')}: ${row.patientName}`,
                                `${t('Accession Number')}: ${row.accessionNumber}`,
                                `${t('Modality')}: ${row.modality}`,
                                `${t('Description')}: ${row.studyDescription}`,
                                `${t('Study Date')}: ${moment(row.studyDate).format('DD-MM-yyyy HH:mm')}`,
                                `${t('Received Date')}: ${moment(row.receivedDate).format('DD-MM-yyyy HH:mm')}`,
                            ].join('\n')
                            await copyToClipboard(text);
                        }}
                    />
                </div>
            ),
        },
        {
            title: t('Status'),
            key: 'status',
            width: 60,
            render: (text, record) => {
                return (
                    <Flex alignItems={'center'}>
                        {/* Liste üyeliği — satır başındaki ilk ikon. Görünür listede değilse hiç çizilmez. */}
                        <StudyListStar row={record} />
                        {record.isReportable === false && (
                            <Tooltip title={t('Not Reportable')}>
                                <LockOutlined style={{color: '#e74c3c'}}/>
                            </Tooltip>
                        )}
                        {record.isRead && (
                            <Tooltip title={t('Read')}>
                                <EyeOutlined style={{color: '#2980b9'}}/>
                            </Tooltip>
                        )}
                        {record.isEmergency && (
                            <Tooltip title={t('Emergency')}>
                                <AlertOutlined style={{color: '#c0392b'}}/>
                            </Tooltip>
                        )}
                        {
                            record.hasVoiceRecord && (
                                <Tooltip title={t('Voice Record')}>
                                    <BsFillMicFill />
                                </Tooltip>
                            )
                        }
                        {
                            record.hasAnamnez && (
                                <Tooltip title={t('Anamnesis')}>
                                    <HiOutlineDocumentText />
                                </Tooltip>
                            )
                        }
                        {
                            record.hasFile && (
                                <Tooltip title={t('File')}>
                                    <BsFileArrowDownFill color={'#3742fa'} />
                                </Tooltip>
                            )
                        }
                        {
                            record.studyTasks?.length > 0 && (
                                <Tooltip 
                                    title={
                                        <div>
                                            {record.studyTasks.map((task, idx) => (
                                                <div key={idx} style={{marginBottom: 4}}>
                                                    <strong>{t(StudyTaskTypeEnum[task.taskType] || `Task Type ${task.taskType}`)}</strong>
                                                    {task.description && <div style={{fontSize: 11, color: '#ccc'}}>{task.description}</div>}
                                                </div>
                                            ))}
                                        </div>
                                    }
                                >
                                    <ExclamationCircleOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
                                </Tooltip>
                            )
                        }
                    </Flex>
                )
            }
        },
        {
            title: t('R/Req'),
            dataIndex: 'info',
            key: 'info',
            width: 50,
            ellipsis: true,
            render: (text, record) => {
                return `${record.reportCount}/${record.requestCount}`
            }
        },
        {
            title: t('Institution'),
            dataIndex: 'institution',
            key: 'institution',
            columnName: 'institution.name',
            sorter: true,
            // 80px'de buton iç boşluk + karetten sonra metne yer bırakmıyordu; kurum adı okunsun.
            width: 120,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <InstitutionCell
                    record={record}
                    canEdit={hasPermission(PERMISSIONS.EDIT_STUDY_INSTITUTION)}
                    onChanged={() => refresh && refresh()}
                />
            )
        },
        {
            title: t('Hospital Name'),
            dataIndex: 'hospitalName',
            key: 'hospitalName',
            columnName: 'pacsSystem.hospital.name',
            sorter: true,
            width: 100,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Patient Id'),
            dataIndex: 'dicomPatientId',
            key: 'dicomPatientId',
            columnName: 'patient.dicomPatientId',
            sorter: true,
            width: 100,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Patient Name'),
            dataIndex: 'patientName',
            key: 'patientName',
            columnName: 'patient.name',
            sorter: true,
            width: 150,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Age'),
            dataIndex: 'birthdate',
            key: 'birthdate',
            columnName: 'patient.birthdate',
            sorter: true,
            width: 30,
            ellipsis: true,
            render: (text, record) => getAge(text),
        },
        {
            title: t('Accession Number'),
            dataIndex: 'accessionNumber',
            key: 'accessionNumber',
            columnName: 'accessionNumber',
            sorter: true,
            width: 80,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Modality'),
            dataIndex: 'modality',
            key: 'modality',
            width: 50,
            columnName: 'modality.name',
            sorter: true,
            ellipsis: true,
        },
        {
            title: t('Description'),
            dataIndex: 'studyDescription',
            key: 'studyDescription',
            sorter: true,
            width: 200,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text}>
                    <span>{text}</span>
                </Tooltip>
            )
        },
        {
            title: t('Read User'),
            dataIndex: 'readUserNames',
            key: 'readUserNames',
            width: 50,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={record.readUserNames || '-'}>
                    <span>{record.readUserNames || '-'}</span>
                </Tooltip>
            )
        },
        {
            title: t('Assigned Doctor'),
            dataIndex: 'assignedDoctorName',
            key: 'assignedDoctorName',
            columnName: 'assignedDoctorName',
            width: 50,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text || '-'}>
                    <span>{text || '-'}</span>
                </Tooltip>
            )
        },
        {
            title: t('Assigned Reporter'),
            dataIndex: 'assignedReporterName',
            key: 'assignedReporterName',
            columnName: 'assignedReporterName',
            width: 50,
            ellipsis: { showTitle: false },
            render: (text, record) => (
                <Tooltip title={text || '-'}>
                    <span>{text || '-'}</span>
                </Tooltip>
            )
        },
        {
            title: t('Study Date'),
            dataIndex: 'studyDate',
            key: 'studyDate',
            columnName: 'studyDateTime',
            sorter: true,
            width: 100,
            ellipsis: true,
            render: (text, record) => dateFormat(text),
        },
        {
            title: t('Received Date'),
            dataIndex: 'receivedDate',
            key: 'receivedDate',
            columnName: 'createdAt',
            width: 100,
            ellipsis: true,
            render: (text, record) => dateFormat(text),
            sorter: true,
        },
        {
            title: t('I-C'),
            dataIndex: 'instanceCount',
            key: 'instanceCount',
            columnName: 'numberOfStudyRelatedInstance',
            width: 50,
            ellipsis: true,
            sorter: true
        },
        {
            title: t('Study Type'),
            dataIndex: 'studyType',
            key: 'studyType',
            columnName: 'StudyDetail.studyType',
            sorter: true,
            width: 70,
            ellipsis: true,
            render: (text, record) => t(EnumHelper.getKeyByValue(StudyTypeEnum, record.studyType))
        },
        {
            title: t('Time'),
            dataIndex: 'studyTimeoutDate',
            key: 'studyTimeoutDate',
            columnName: 'studyDetail.timeoutDate',
            sorter: true,
            width: 60,
            ellipsis: true,
            render: (text, record) => {
                let minStr = 0;

                let now = new Date().getTime();
                if(record.studyStatus)
                    now = new Date(record.studyDetailUpdateDate)

                let timeoutDate = new Date(record.studyTimeoutDate).getTime();
                let remain = timeoutDate - now;

                let sec = remain/1000;
                let min = parseInt(sec / 60);
                minStr = parseInt(min.toString()) + t('m')

                if(min > 240) {
                    minStr = '-'
                }

                switch (record.studyStatus) {
                    case 2:
                        return <CheckOutlined />
                    case 1:
                        return (
                            <Tooltip title={minStr} placement={'bottom'}>
                                <FieldTimeOutlined />
                            </Tooltip>
                        )
                    default:
                        return minStr
                }
            }
        },
    ];

    if(!hasPermission(PERMISSIONS.STUDY_GRID_USER_INFO)) {
        columns = columns.filter(x => x.dataIndex !== 'readUserNames')
        columns = columns.filter(x => x.dataIndex !== 'assignedDoctorName')
        columns = columns.filter(x => x.dataIndex !== 'assignedReporterName')
    }

    if(!hasPermission(PERMISSIONS.VIEW_STUDY_INSTITUTION)) {
        columns = columns.filter(x => x.dataIndex !== 'institution')
    }

    return columns
}

export default studyGridColumns;
